import React, { MouseEvent, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { ColumnInstance, IdType } from 'react-table';

import { useTranslation } from '@fluentsms/i18n';
import {
  Button,
  createStyles,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  makeStyles,
  Menu,
  MenuItem,
  Switch,
  Theme,
  Tooltip,
} from '@material-ui/core';
import { DragHandle, Settings } from '@material-ui/icons';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    headerIcon: {
      '&&': {
        padding: theme.spacing(1.5),
        fontSize: theme.spacing(3),
      },
    },
    buttonItem: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    stickyHead: {
      backgroundColor: theme.palette.background.paper,
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
    droppableContainer: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    columnMargin: {
      marginRight: theme.spacing(1),
    },
    resetButton: {
      width: theme.spacing(10),
      alignSelf: 'flex-end',
      margin: theme.spacing(1, 2, 1),
    },
  }),
);

export type DataGridSettingsMenuProps<T extends Record<string, unknown>> = {
  columns?: ColumnInstance<T>[];
  columnOrderId?: IdType<T>[];
  setChangeColumnVisibility?: (toggleColumn: ColumnInstance<T>) => void;
  setChangeColumnOrder?: (reorderedColumns: IdType<T>[]) => void;
  onReset?: () => void;
};

export function DataGridSettingsMenu<T extends Record<string, unknown>>({
  columns = [],
  columnOrderId = [],
  setChangeColumnVisibility = () => undefined,
  setChangeColumnOrder = () => undefined,
  onReset = () => undefined,
}: DataGridSettingsMenuProps<T>): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dragging, setDragging] = useState(false);
  const classes = useStyles();
  const { t }: { t: any } = useTranslation('common'); // eslint-disable-line @typescript-eslint/no-explicit-any
  const resetButton = t('dataGridSettingsMenu.resetButton');

  return (
    <>
      <Tooltip title="Settings" arrow placement="top" data-testid="data-grid-settings-tooltip">
        <IconButton
          className={classes.headerIcon}
          size="medium"
          onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
        >
          <Settings />
        </IconButton>
      </Tooltip>
      <Menu
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        data-testid="data-grid-settings-menu"
      >
        <div tabIndex={-1}>
          <DragDropContext
            onDragStart={() => {
              setDragging(true);
            }}
            onDragEnd={(dropResult: DropResult) => {
              setDragging(false);
              if (!dropResult.destination) return;

              const columnOrder = columnOrderId;
              const startIndex = dropResult.source.index;
              const destinationIndex = dropResult.destination.index;

              columnOrder.splice(startIndex, 1);
              columnOrder.splice(destinationIndex, 0, dropResult.draggableId);

              setChangeColumnOrder(columnOrder);
            }}
          >
            <ListSubheader className={classes.stickyHead} data-testid="data-grid-settings-header">
              Display Columns
            </ListSubheader>
            <Droppable droppableId="columnReordered" direction="vertical">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className={classes.droppableContainer}>
                  {columns.map((column, columnIndex) => {
                    if (column && column.id && column.id !== 'Menu' && column.id !== 'Selection') {
                      return (
                        <Draggable key={column.id} draggableId={column.id.toString()} index={columnIndex}>
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onKeyDown={(event: React.KeyboardEvent) => {
                                switch (event.key) {
                                  case 'ArrowLeft':
                                  case 'ArrowRight':
                                    if (!dragging) {
                                      event.preventDefault();
                                      setChangeColumnVisibility(column);
                                    }
                                    break;
                                  default:
                                    break;
                                }
                              }}
                            >
                              <MenuItem>
                                <ListItemIcon>
                                  <DragHandle />
                                </ListItemIcon>
                                <ListItemText
                                  className={classes.columnMargin}
                                  data-testid={`data-grid-settings-column-name-${columnIndex}`}
                                >
                                  {column.Header}
                                </ListItemText>
                                <Switch
                                  id={column.id}
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    setChangeColumnVisibility(column);
                                  }}
                                  checked={column.isVisible}
                                  data-testid={`data-grid-settings-column-toggle-${columnIndex}`}
                                />
                              </MenuItem>
                            </div>
                          )}
                        </Draggable>
                      );
                    }
                  })}
                  {provided.placeholder}
                  <Button
                    variant="outlined"
                    onClick={onReset}
                    data-testid="data-grid-settings-reset-button"
                    className={classes.resetButton}
                    color="secondary"
                  >
                    {resetButton}
                  </Button>
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </Menu>
    </>
  );
}

export default DataGridSettingsMenu;
